import React, { useState } from "react";
import { createPortal } from "react-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { toast } from "react-toastify";
import axiosInstance from "../Context/axiosInstance";
import { isHidden } from "../utils/visibility";

export default function VisibilityToggle({
  item,
  endpoint,
  visibilityEndpoint,
  requestBody,
  confirmHide = false,
  successMessage,
  onChange,
}) {
  const [saving, setSaving] = useState(false);
  const [showHideConfirmation, setShowHideConfirmation] = useState(false);
  const hidden = isHidden(item);
  const itemLabel = item.name || item.title || "item";

  const updateVisibility = async (nextHidden) => {
    setSaving(true);
    try {
      if (visibilityEndpoint) {
        await axiosInstance.post(
          `${visibilityEndpoint}/${nextHidden ? "hide" : "unhide"}`,
          requestBody || { ids: [item.id] },
        );
      }

      void endpoint;
      onChange?.({
        ...item,
        isHidden: nextHidden,
        hidden: nextHidden,
        isVisible: !nextHidden,
      });

      toast.success(
          typeof successMessage === "function"
            ? successMessage(nextHidden)
            : successMessage || `${itemLabel} ${nextHidden ? "hidden" : "shown"} successfully.`,
      );
    } catch (error) {
      toast.error(
        error.response?.data?.errors?.[0]?.reason ||
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Could not update visibility.",
      );
    } finally {
      setSaving(false);
    }
  };

  const toggle = () => {
    const nextHidden = !hidden;
    if (nextHidden && confirmHide) {
      setShowHideConfirmation(true);
      return;
    }
    updateVisibility(nextHidden);
  };

  return (
    <>
      {showHideConfirmation &&
        createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-sm overflow-y-auto">
          <div
            className="w-full max-w-md rounded-2xl bg-white p-7 text-gray-900 shadow-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`hide-${item.id}-title`}
          >
            <h2
              id={`hide-${item.id}-title`}
              className="text-center text-xl font-bold"
            >
              Hide this course?
            </h2>
            <p className="mt-3 text-center text-sm leading-6 text-gray-600">
              <span className="font-semibold">{itemLabel}</span> will no longer
              be visible to students. You can make it visible again later.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setShowHideConfirmation(false)}
                className="rounded-xl bg-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-800 transition hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowHideConfirmation(false);
                  updateVisibility(true);
                }}
                className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700"
              >
                Hide course
              </button>
            </div>
          </div>
        </div>,
          document.body,
        )}

      <button
        type="button"
        onClick={toggle}
        disabled={saving}
        aria-pressed={!hidden}
        aria-label={`${hidden ? "Show" : "Hide"} ${itemLabel}`}
        title={
          hidden
            ? "Hidden from students — click to show"
            : "Visible to students — click to hide"
        }
        className={`group inline-flex min-w-[112px] items-center justify-between gap-2 rounded-full border px-2 py-1.5 text-xs font-semibold shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-wait disabled:opacity-60 ${
          hidden
            ? "border-gray-300 bg-gray-100 text-gray-600 hover:border-gray-400 hover:bg-gray-200 focus:ring-gray-400"
            : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100 focus:ring-emerald-500"
        }`}
      >
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white shadow-sm transition-transform duration-200 group-hover:scale-105 ${
            hidden ? "bg-gray-500" : "bg-emerald-500"
          }`}
        >
          {saving ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : hidden ? (
            <FaEyeSlash aria-hidden="true" />
          ) : (
            <FaEye aria-hidden="true" />
          )}
        </span>
        <span className="flex-1 text-center">
          {saving ? "Saving" : hidden ? "Hidden" : "Visible"}
        </span>
      </button>
    </>
  );
}
